import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllSundayRosts,
  getSingleSundayRoast,
  createSundayRoast,
  updateSundayRoast,
  deleteSundayRoast,
} from "@/api/new-api/staff-management/menu-manage/special-menu/sunday-roast";

// Thunks
export const fetchAllSundayRosts = createAsyncThunk(
  "sundayRoast/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllSundayRosts(restaurant_id);
    if (res.status === "success") return res.data.sunday_roasts;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleSundayRoast = createAsyncThunk(
  "sundayRoast/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleSundayRoast(id);
    if (res.status === "success") return res.data.sunday_roast;
    return rejectWithValue(res.message);
  }
);

export const createNewSundayRoast = createAsyncThunk(
  "sundayRoast/create",
  async (data, { rejectWithValue }) => {
    const res = await createSundayRoast(data);
    if (res.status === "success") return res.data.sunday_roast;
    return rejectWithValue(res.message);
  }
);

export const updateExistingSundayRoast = createAsyncThunk(
  "sundayRoast/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateSundayRoast(id, data);
    if (res.status === "success") return res.data.sunday_roast;
    return rejectWithValue(res.message);
  }
);

export const deleteSundayRoastById = createAsyncThunk(
  "sundayRoast/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteSundayRoast(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  sundayRosts: [],
  singleSundayRoast: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const sundayRoastSlice = createSlice({
  name: "sundayRoast",
  initialState,
  reducers: {
    clearSundayRoastMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllSundayRosts.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllSundayRosts.fulfilled, (state, action) => {
        state.getLoading = false;
        state.sundayRosts = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllSundayRosts.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleSundayRoast.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleSundayRoast.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleSundayRoast = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleSundayRoast.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewSundayRoast.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewSundayRoast.fulfilled, (state, action) => {
        state.createLoading = false;
        state.sundayRosts.push(action.payload);
        state.success = "Sunday Roast created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewSundayRoast.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingSundayRoast.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingSundayRoast.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.sundayRosts = state.sundayRosts.map((dish) =>
          dish.id === action.payload.id ? action.payload : dish
        );
        state.success = "Sunday Roast updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingSundayRoast.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteSundayRoastById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteSundayRoastById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.sundayRosts = state.sundayRosts.filter(
          (dish) => dish.id !== action.payload
        );
        state.success = "Sunday Roast deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteSundayRoastById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearSundayRoastMessages } = sundayRoastSlice.actions;
export default sundayRoastSlice.reducer;
